/**
 * Pictures for the things that sit on a tile — wheat, fish, ore, horses.
 *
 * Mark's words: "change the tile additions (corn, fish etc) to be more than just a dot".
 * A coloured dot told you *that* a tile had something on it and made you hover to learn
 * *what*. A picture tells you what at a glance, which is the whole job of a resource
 * marker: the player is scanning for the site worth settling, not reading tooltips.
 *
 * Same visual language as the relief in cartography: flat fills, one dark ink outline,
 * light from the north-west, a shadow on the ground under anything standing on land and
 * a ripple under anything in the water. Drawn in code, like everything else — no image
 * assets, nothing to license, crisp at every zoom and every pixel density.
 *
 * WHICH picture a resource gets is content (`icon` in resources.json); HOW each picture
 * is drawn is here. `DRAWERS` is typed as a complete record over `ResourceIcon`, so
 * adding a picture name to the schema without drawing it fails to compile.
 *
 * Every drawer works in units of `s`, roughly the icon's radius, centred on (x, y), and
 * stays within about ±s horizontally and −s…+0.9s vertically.
 */

import type { ResourceIcon } from '../../content/schema/index.js';
import { parseHex, shade, toCss } from './cartography.js';

type Ctx = CanvasRenderingContext2D;
type Drawer = (ctx: Ctx, x: number, y: number, s: number, colour: string) => void;

/** The one outline colour every icon shares. A near-black with some warmth in it. */
const INK = 'rgba(24, 18, 12, 0.88)';

function tone(colour: string, amount: number): string {
  return toCss(shade(parseHex(colour), amount));
}

/** Stroke the current path in ink, at a weight that scales with the icon. */
function ink(ctx: Ctx, s: number, weight = 0.09): void {
  ctx.strokeStyle = INK;
  ctx.lineWidth = Math.max(1, s * weight);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
}

function dot(ctx: Ctx, x: number, y: number, r: number, fill: string): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
}

function polygon(ctx: Ctx, x: number, y: number, s: number, points: ReadonlyArray<readonly [number, number]>): void {
  ctx.beginPath();
  points.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(x + px * s, y + py * s) : ctx.lineTo(x + px * s, y + py * s)));
  ctx.closePath();
}

function roundedRect(ctx: Ctx, left: number, top: number, width: number, height: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(left + r, top);
  ctx.arcTo(left + width, top, left + width, top + height, r);
  ctx.arcTo(left + width, top + height, left, top + height, r);
  ctx.arcTo(left, top + height, left, top, r);
  ctx.arcTo(left, top, left + width, top, r);
  ctx.closePath();
}

// ─── food ──────────────────────────────────────────────────────────────────────

/** A sheaf: three stalks fanning up from a tie, each with a full head of grain. */
const wheat: Drawer = (ctx, x, y, s, colour) => {
  const base = { x, y: y + s * 0.85 };
  const angles = [-0.34, 0, 0.34];

  ctx.strokeStyle = tone(colour, -0.45);
  ctx.lineWidth = Math.max(1, s * 0.08);
  ctx.lineCap = 'round';
  for (const a of angles) {
    ctx.beginPath();
    ctx.moveTo(base.x, base.y);
    ctx.lineTo(base.x + Math.sin(a) * s * 1.05, base.y - Math.cos(a) * s * 1.05);
    ctx.stroke();
  }

  for (const a of angles) {
    ctx.save();
    ctx.translate(base.x + Math.sin(a) * s * 1.25, base.y - Math.cos(a) * s * 1.25);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.ellipse(0, 0, s * 0.17, s * 0.42, 0, 0, Math.PI * 2);
    ctx.fillStyle = colour;
    ctx.fill();
    ink(ctx, s, 0.07);
    // The kernels: a few short slanted ticks, which is what makes it grain and not a leaf.
    ctx.strokeStyle = tone(colour, -0.32);
    ctx.lineWidth = Math.max(0.8, s * 0.05);
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(-s * 0.12, i * s * 0.18 + s * 0.04);
      ctx.lineTo(s * 0.12, i * s * 0.18 - s * 0.06);
      ctx.stroke();
    }
    ctx.restore();
  }

  // The tie round the sheaf.
  roundedRect(ctx, x - s * 0.16, y + s * 0.38, s * 0.32, s * 0.13, s * 0.05);
  ctx.fillStyle = tone(colour, -0.2);
  ctx.fill();
  ink(ctx, s, 0.06);
};

/** A fish in profile, with a pale belly — the belly is what makes it read as a fish at size. */
const fish: Drawer = (ctx, x, centre, s, colour) => {
  // A fish is wider than it is tall, so centred like everything else it floats well
  // clear of its ripple. Dropped a little so it sits *in* the water.
  const y = centre + s * 0.28;
  const fin = tone(colour, -0.22);

  ctx.beginPath();
  ctx.moveTo(x + s * 0.45, y);
  ctx.lineTo(x + s * 0.95, y - s * 0.38);
  ctx.quadraticCurveTo(x + s * 0.8, y, x + s * 0.95, y + s * 0.38);
  ctx.closePath();
  ctx.fillStyle = fin;
  ctx.fill();
  ink(ctx, s, 0.07);

  ctx.beginPath();
  ctx.moveTo(x - s * 0.34, y - s * 0.3);
  ctx.quadraticCurveTo(x - s * 0.05, y - s * 0.66, x + s * 0.22, y - s * 0.28);
  ctx.closePath();
  ctx.fillStyle = fin;
  ctx.fill();
  ink(ctx, s, 0.07);

  ctx.beginPath();
  ctx.ellipse(x - s * 0.12, y, s * 0.66, s * 0.36, 0, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s);

  ctx.beginPath();
  ctx.ellipse(x - s * 0.16, y + s * 0.13, s * 0.46, s * 0.13, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.38)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x - s * 0.56, y, s * 0.26, -0.9, 0.9);
  ink(ctx, s, 0.05);
  dot(ctx, x - s * 0.52, y - s * 0.08, s * 0.065, INK);
};

/** A cow's head, face on: horns, ears, a pale muzzle. */
const cattle: Drawer = (ctx, x, y, s, colour) => {
  const horn = (d: number): void => {
    ctx.beginPath();
    ctx.moveTo(x + d * s * 0.26, y - s * 0.36);
    ctx.quadraticCurveTo(x + d * s * 0.86, y - s * 0.42, x + d * s * 0.76, y - s * 0.9);
  };
  for (const d of [-1, 1]) {
    horn(d);
    ctx.strokeStyle = INK;
    ctx.lineWidth = Math.max(2, s * 0.2);
    ctx.lineCap = 'round';
    ctx.stroke();
    horn(d);
    ctx.strokeStyle = '#EDE3CA';
    ctx.lineWidth = Math.max(1, s * 0.11);
    ctx.stroke();
  }

  for (const d of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(x + d * s * 0.54, y - s * 0.14, s * 0.2, s * 0.1, d * 0.35, 0, Math.PI * 2);
    ctx.fillStyle = tone(colour, -0.18);
    ctx.fill();
    ink(ctx, s, 0.07);
  }

  ctx.beginPath();
  ctx.ellipse(x, y, s * 0.4, s * 0.52, 0, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s);

  // A blaze down the forehead. Without some pale on the face, a brown oval is a potato.
  ctx.beginPath();
  ctx.ellipse(x, y - s * 0.16, s * 0.11, s * 0.2, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(246, 238, 222, 0.82)';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(x, y + s * 0.38, s * 0.31, s * 0.2, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#D9B8A0';
  ctx.fill();
  ink(ctx, s, 0.07);

  dot(ctx, x - s * 0.12, y + s * 0.4, s * 0.045, INK);
  dot(ctx, x + s * 0.12, y + s * 0.4, s * 0.045, INK);
  dot(ctx, x - s * 0.2, y - s * 0.02, s * 0.06, INK);
  dot(ctx, x + s * 0.2, y - s * 0.02, s * 0.06, INK);
};

/** A deer's head with antlers — game, in the sense of hunting rather than playing. */
const deer: Drawer = (ctx, x, y, s, colour) => {
  const antlers = (d: number): void => {
    ctx.beginPath();
    ctx.moveTo(x + d * s * 0.12, y - s * 0.22);
    ctx.quadraticCurveTo(x + d * s * 0.45, y - s * 0.45, x + d * s * 0.55, y - s * 0.98);
    ctx.moveTo(x + d * s * 0.33, y - s * 0.44);
    ctx.lineTo(x + d * s * 0.74, y - s * 0.56);
    ctx.moveTo(x + d * s * 0.48, y - s * 0.72);
    ctx.lineTo(x + d * s * 0.3, y - s * 0.96);
  };
  for (const d of [-1, 1]) {
    antlers(d);
    ctx.strokeStyle = INK;
    ctx.lineWidth = Math.max(2, s * 0.17);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    antlers(d);
    ctx.strokeStyle = '#E6D6B6';
    ctx.lineWidth = Math.max(1, s * 0.08);
    ctx.stroke();
  }

  for (const d of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(x + d * s * 0.36, y - s * 0.06, s * 0.2, s * 0.09, d * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = tone(colour, -0.12);
    ctx.fill();
    ink(ctx, s, 0.07);
  }

  ctx.beginPath();
  ctx.moveTo(x - s * 0.28, y - s * 0.2);
  ctx.quadraticCurveTo(x, y - s * 0.38, x + s * 0.28, y - s * 0.2);
  ctx.quadraticCurveTo(x + s * 0.26, y + s * 0.35, x + s * 0.1, y + s * 0.72);
  ctx.quadraticCurveTo(x, y + s * 0.8, x - s * 0.1, y + s * 0.72);
  ctx.quadraticCurveTo(x - s * 0.26, y + s * 0.35, x - s * 0.28, y - s * 0.2);
  ctx.closePath();
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s);

  ctx.beginPath();
  ctx.ellipse(x, y + s * 0.67, s * 0.09, s * 0.07, 0, 0, Math.PI * 2);
  ctx.fillStyle = INK;
  ctx.fill();
  dot(ctx, x - s * 0.14, y + s * 0.06, s * 0.055, INK);
  dot(ctx, x + s * 0.14, y + s * 0.06, s * 0.055, INK);
};

// ─── strategic ─────────────────────────────────────────────────────────────────

/** A horse's head and neck in profile, with a darker mane. */
const horse: Drawer = (ctx, x, y, s, colour) => {
  const at = (px: number, py: number): [number, number] => [x + px * s, y + py * s];

  // Mane first, so the neck covers its inner edge and only the crest shows.
  ctx.beginPath();
  ctx.moveTo(...at(0.2, -0.62));
  ctx.quadraticCurveTo(...at(0.6, -0.02), ...at(0.62, 0.92));
  ctx.lineTo(...at(0.78, 0.92));
  ctx.quadraticCurveTo(...at(0.78, -0.08), ...at(0.28, -0.7));
  ctx.closePath();
  ctx.fillStyle = tone(colour, -0.48);
  ctx.fill();
  ink(ctx, s, 0.06);

  ctx.beginPath();
  ctx.moveTo(...at(0.62, 0.92));
  ctx.quadraticCurveTo(...at(0.6, 0.05), ...at(0.2, -0.62));
  ctx.lineTo(...at(0.24, -0.96));
  ctx.lineTo(...at(0.04, -0.66));
  ctx.quadraticCurveTo(...at(-0.3, -0.55), ...at(-0.62, -0.05));
  ctx.quadraticCurveTo(...at(-0.84, 0.22), ...at(-0.66, 0.34));
  ctx.quadraticCurveTo(...at(-0.45, 0.42), ...at(-0.2, 0.22));
  ctx.quadraticCurveTo(...at(-0.02, 0.35), ...at(0.04, 0.92));
  ctx.closePath();
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s);

  // The line of the cheek: one curve, and the head stops being a flat cut-out.
  ctx.beginPath();
  ctx.moveTo(...at(-0.24, 0.2));
  ctx.quadraticCurveTo(...at(0.06, 0.1), ...at(0.02, -0.2));
  ink(ctx, s, 0.05);

  dot(ctx, ...at(-0.2, -0.3), s * 0.065, INK);
  dot(ctx, ...at(-0.64, 0.14), s * 0.05, INK);
};

/**
 * A boulder with ore showing in it. Copper, iron and coal all use this and are told
 * apart by the ore's colour — the tooltip names it, and the eye needs only "metal here".
 */
const ore: Drawer = (ctx, x, y, s, colour) => {
  const rock = '#8A8174';
  const outline: Array<[number, number]> = [
    [-0.86, 0.6],
    [-0.62, -0.08],
    [-0.24, -0.52],
    [0.3, -0.56],
    [0.76, -0.1],
    [0.9, 0.6],
  ];

  polygon(ctx, x, y, s, outline);
  ctx.fillStyle = rock;
  ctx.fill();

  polygon(ctx, x, y, s, [
    [-0.62, -0.08],
    [-0.24, -0.52],
    [0.3, -0.56],
    [0.1, -0.04],
  ]);
  ctx.fillStyle = tone(rock, 0.26);
  ctx.fill();

  polygon(ctx, x, y, s, [
    [0.3, -0.56],
    [0.76, -0.1],
    [0.9, 0.6],
    [0.36, 0.6],
    [0.1, -0.04],
  ]);
  ctx.fillStyle = tone(rock, -0.26);
  ctx.fill();

  polygon(ctx, x, y, s, outline);
  ink(ctx, s);

  // Veins of ore: irregular nuggets, each with a glint.
  const nuggets: Array<[number, number, number]> = [
    [-0.4, 0.24, 0.19],
    [0.14, 0.3, 0.15],
    [0.46, 0.04, 0.12],
    [-0.08, -0.2, 0.12],
  ];
  for (const [nx, ny, r] of nuggets) {
    polygon(ctx, x + nx * s, y + ny * s, r * s, [
      [-1, 0.1],
      [-0.4, -0.9],
      [0.6, -0.8],
      [1, 0.2],
      [0.3, 0.95],
      [-0.7, 0.8],
    ]);
    ctx.fillStyle = colour;
    ctx.fill();
    ink(ctx, s, 0.055);
    dot(ctx, x + (nx - r * 0.3) * s, y + (ny - r * 0.35) * s, r * s * 0.28, 'rgba(255, 255, 255, 0.55)');
  }
};

/** A cluster of crystal prisms: lit faces left, shaded faces right. */
const crystals: Drawer = (ctx, x, y, s, colour) => {
  const prism = (cx: number, base: number, w: number, h: number, lean: number): void => {
    const top = { x: cx + lean * s, y: base - h * s };
    const shoulder = base - h * s * 0.72;

    ctx.beginPath();
    ctx.moveTo(cx - w * s, base);
    ctx.lineTo(cx - w * s + lean * s, shoulder);
    ctx.lineTo(top.x, top.y);
    ctx.lineTo(cx + lean * s * 0.9, shoulder + s * 0.04);
    ctx.lineTo(cx, base);
    ctx.closePath();
    ctx.fillStyle = tone(colour, 0.28);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(cx, base);
    ctx.lineTo(cx + lean * s * 0.9, shoulder + s * 0.04);
    ctx.lineTo(top.x, top.y);
    ctx.lineTo(cx + w * s + lean * s, shoulder);
    ctx.lineTo(cx + w * s, base);
    ctx.closePath();
    ctx.fillStyle = tone(colour, -0.22);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(cx - w * s, base);
    ctx.lineTo(cx - w * s + lean * s, shoulder);
    ctx.lineTo(top.x, top.y);
    ctx.lineTo(cx + w * s + lean * s, shoulder);
    ctx.lineTo(cx + w * s, base);
    ctx.closePath();
    ink(ctx, s, 0.07);
  };

  const base = y + s * 0.72;
  prism(x - s * 0.44, base, 0.2, 1.0, -0.14);
  prism(x + s * 0.42, base, 0.2, 0.92, 0.16);
  prism(x, base + s * 0.06, 0.27, 1.55, 0.02);
};

// ─── luxuries ──────────────────────────────────────────────────────────────────

/** A cut stone, side on: crown and pavilion facets, and a glint. */
const gem: Drawer = (ctx, x, y, s, colour) => {
  const wide = s * 0.8;
  const table = s * 0.42;
  const crown = y - s * 0.46;
  const girdle = y - s * 0.1;
  const point = y + s * 0.82;
  const inner = s * 0.26;

  const facet = (points: Array<[number, number]>, amount: number): void => {
    ctx.beginPath();
    points.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
    ctx.closePath();
    ctx.fillStyle = tone(colour, amount);
    ctx.fill();
  };

  facet([[x - table, crown], [x - wide, girdle], [x - inner, girdle]], 0.38);
  facet([[x - table, crown], [x + table, crown], [x + inner, girdle], [x - inner, girdle]], 0.16);
  facet([[x + table, crown], [x + wide, girdle], [x + inner, girdle]], -0.08);
  facet([[x - wide, girdle], [x - inner, girdle], [x, point]], 0.06);
  facet([[x - inner, girdle], [x + inner, girdle], [x, point]], -0.16);
  facet([[x + inner, girdle], [x + wide, girdle], [x, point]], -0.36);

  ctx.beginPath();
  ctx.moveTo(x - table, crown);
  ctx.lineTo(x + table, crown);
  ctx.lineTo(x + wide, girdle);
  ctx.lineTo(x, point);
  ctx.lineTo(x - wide, girdle);
  ctx.closePath();
  ink(ctx, s);

  ctx.beginPath();
  ctx.moveTo(x - wide, girdle);
  ctx.lineTo(x + wide, girdle);
  ink(ctx, s, 0.045);

  // A four-point glint on the lit side of the table.
  const gx = x - s * 0.24;
  const gy = crown + s * 0.14;
  const g = s * 0.18;
  ctx.beginPath();
  ctx.moveTo(gx, gy - g);
  ctx.lineTo(gx + g * 0.22, gy - g * 0.22);
  ctx.lineTo(gx + g, gy);
  ctx.lineTo(gx + g * 0.22, gy + g * 0.22);
  ctx.lineTo(gx, gy + g);
  ctx.lineTo(gx - g * 0.22, gy + g * 0.22);
  ctx.lineTo(gx - g, gy);
  ctx.lineTo(gx - g * 0.22, gy - g * 0.22);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fill();
};

/** A wooden bowl heaped with ground spice. */
const spice: Drawer = (ctx, x, y, s, colour) => {
  const wood = '#7A5230';

  ctx.beginPath();
  ctx.moveTo(x - s * 0.74, y + s * 0.12);
  ctx.quadraticCurveTo(x - s * 0.24, y - s * 0.9, x + s * 0.1, y - s * 0.56);
  ctx.quadraticCurveTo(x + s * 0.46, y - s * 0.5, x + s * 0.74, y + s * 0.12);
  ctx.closePath();
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s);

  ctx.beginPath();
  ctx.ellipse(x - s * 0.22, y - s * 0.34, s * 0.2, s * 0.09, -0.55, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 224, 186, 0.38)';
  ctx.fill();

  const speck = tone(colour, -0.4);
  for (const [px, py] of [[-0.3, -0.05], [0.05, -0.28], [0.3, -0.06], [-0.05, 0.02], [0.44, -0.2]] as const) {
    dot(ctx, x + px * s, y + py * s, s * 0.04, speck);
  }

  ctx.beginPath();
  ctx.moveTo(x - s * 0.88, y + s * 0.06);
  ctx.quadraticCurveTo(x - s * 0.82, y + s * 0.82, x, y + s * 0.82);
  ctx.quadraticCurveTo(x + s * 0.82, y + s * 0.82, x + s * 0.88, y + s * 0.06);
  ctx.closePath();
  ctx.fillStyle = wood;
  ctx.fill();
  ink(ctx, s);

  ctx.beginPath();
  ctx.ellipse(x - s * 0.3, y + s * 0.36, s * 0.26, s * 0.08, -0.25, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 230, 190, 0.2)';
  ctx.fill();
};

/** A stack of folded cloth, each fold a different shade of the dye. */
const silk: Drawer = (ctx, x, y, s, colour) => {
  const folds = [
    { dy: 0.46, amount: -0.26, w: 0.84 },
    { dy: 0.1, amount: 0, w: 0.78 },
    { dy: -0.26, amount: 0.22, w: 0.7 },
  ];
  for (const fold of folds) {
    const h = s * 0.34;
    const top = y + fold.dy * s - h / 2;
    const half = fold.w * s;
    roundedRect(ctx, x - half, top, half * 2, h, h * 0.45);
    ctx.fillStyle = tone(colour, fold.amount);
    ctx.fill();
    ink(ctx, s, 0.075);

    ctx.beginPath();
    ctx.moveTo(x - half + h * 0.45, top + h * 0.3);
    ctx.lineTo(x + half - h * 0.45, top + h * 0.3);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.42)';
    ctx.lineWidth = Math.max(0.8, s * 0.05);
    ctx.stroke();
  }
};

/** A bronze burner on three legs, resin glowing in it and smoke curling up. */
const incense: Drawer = (ctx, x, y, s, colour) => {
  const bronze = '#8A6A3A';

  ctx.strokeStyle = tone(bronze, -0.3);
  ctx.lineWidth = Math.max(1, s * 0.1);
  ctx.lineCap = 'round';
  for (const [top, foot] of [[-0.34, -0.52], [0, 0], [0.34, 0.52]] as const) {
    ctx.beginPath();
    ctx.moveTo(x + top * s, y + s * 0.5);
    ctx.lineTo(x + foot * s, y + s * 0.88);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.moveTo(x - s * 0.62, y + s * 0.12);
  ctx.quadraticCurveTo(x, y + s * 0.95, x + s * 0.62, y + s * 0.12);
  ctx.closePath();
  ctx.fillStyle = bronze;
  ctx.fill();
  ink(ctx, s);

  ctx.beginPath();
  ctx.ellipse(x, y + s * 0.12, s * 0.62, s * 0.14, 0, 0, Math.PI * 2);
  ctx.fillStyle = tone(bronze, -0.4);
  ctx.fill();
  ink(ctx, s, 0.07);

  ctx.beginPath();
  ctx.ellipse(x, y + s * 0.08, s * 0.32, s * 0.08, 0, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();

  ctx.strokeStyle = 'rgba(242, 238, 230, 0.85)';
  ctx.lineWidth = Math.max(1, s * 0.09);
  for (const [ox, h] of [[-0.12, 1.02], [0.16, 0.84]] as const) {
    ctx.beginPath();
    ctx.moveTo(x + ox * s, y + s * 0.02);
    ctx.bezierCurveTo(x + (ox + 0.32) * s, y - s * 0.28, x + (ox - 0.32) * s, y - s * 0.56, x + (ox + 0.1) * s, y - h * s);
    ctx.stroke();
  }
};

/** An open shell, ridged, with the pearl sitting in it. */
const pearl: Drawer = (ctx, x, y, s, colour) => {
  const shell = '#E3D7C2';
  const hinge = { x, y: y + s * 0.52 };

  ctx.beginPath();
  ctx.moveTo(hinge.x, hinge.y);
  ctx.arc(hinge.x, hinge.y, s * 0.92, Math.PI * 1.07, Math.PI * 1.93);
  ctx.closePath();
  ctx.fillStyle = tone(shell, -0.08);
  ctx.fill();
  ink(ctx, s);

  ctx.strokeStyle = tone(shell, -0.38);
  ctx.lineWidth = Math.max(0.8, s * 0.05);
  for (const a of [1.2, 1.35, 1.5, 1.65, 1.8]) {
    ctx.beginPath();
    ctx.moveTo(hinge.x, hinge.y);
    ctx.lineTo(hinge.x + Math.cos(a * Math.PI) * s * 0.86, hinge.y + Math.sin(a * Math.PI) * s * 0.86);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.ellipse(x, hinge.y, s * 0.8, s * 0.24, 0, 0, Math.PI * 2);
  ctx.fillStyle = shell;
  ctx.fill();
  ink(ctx, s, 0.07);

  dot(ctx, x, y + s * 0.3, s * 0.27, colour);
  ctx.beginPath();
  ctx.arc(x, y + s * 0.3, s * 0.27, 0, Math.PI * 2);
  ink(ctx, s, 0.06);
  dot(ctx, x - s * 0.09, y + s * 0.21, s * 0.08, 'rgba(255, 255, 255, 0.9)');
};

/** Three cut blocks, stacked: front, top and side faces, lit from the north-west. */
const stone: Drawer = (ctx, x, y, s, colour) => {
  const block = (left: number, top: number, w: number, h: number): void => {
    const d = h * 0.36;
    ctx.beginPath();
    ctx.rect(left, top, w, h);
    ctx.fillStyle = colour;
    ctx.fill();
    ink(ctx, s, 0.07);

    ctx.beginPath();
    ctx.moveTo(left, top);
    ctx.lineTo(left + d, top - d);
    ctx.lineTo(left + w + d, top - d);
    ctx.lineTo(left + w, top);
    ctx.closePath();
    ctx.fillStyle = tone(colour, 0.3);
    ctx.fill();
    ink(ctx, s, 0.07);

    ctx.beginPath();
    ctx.moveTo(left + w, top);
    ctx.lineTo(left + w + d, top - d);
    ctx.lineTo(left + w + d, top + h - d);
    ctx.lineTo(left + w, top + h);
    ctx.closePath();
    ctx.fillStyle = tone(colour, -0.32);
    ctx.fill();
    ink(ctx, s, 0.07);
  };

  block(x - s * 0.88, y + s * 0.22, s * 0.8, s * 0.5);
  block(x + s * 0.0, y + s * 0.22, s * 0.72, s * 0.5);
  block(x - s * 0.48, y - s * 0.34, s * 0.8, s * 0.5);
};

/** A dark pool on the ground with a drop rising from it, glossy where the light catches. */
const oil: Drawer = (ctx, x, y, s, colour) => {
  ctx.beginPath();
  ctx.ellipse(x, y + s * 0.55, s * 0.78, s * 0.24, 0, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s, 0.07);
  ctx.beginPath();
  ctx.ellipse(x - s * 0.3, y + s * 0.5, s * 0.2, s * 0.06, 0, 0, Math.PI * 2);
  ctx.fillStyle = tone(colour, 0.55);
  ctx.fill();

  // The drop: a point at the top, round at the bottom.
  ctx.beginPath();
  ctx.moveTo(x, y - s * 0.85);
  ctx.bezierCurveTo(x + s * 0.12, y - s * 0.55, x + s * 0.48, y - s * 0.18, x + s * 0.48, y + s * 0.08);
  ctx.arc(x, y + s * 0.08, s * 0.48, 0, Math.PI, false);
  ctx.bezierCurveTo(x - s * 0.48, y - s * 0.18, x - s * 0.12, y - s * 0.55, x, y - s * 0.85);
  ctx.closePath();
  ctx.fillStyle = colour;
  ctx.fill();
  ink(ctx, s, 0.09);
  ctx.beginPath();
  ctx.ellipse(x - s * 0.2, y - s * 0.02, s * 0.09, s * 0.2, -0.3, 0, Math.PI * 2);
  ctx.fillStyle = tone(colour, 0.65);
  ctx.fill();
};

const nothing: Drawer = () => {};

/**
 * Every picture the content may ask for. A complete record, so the compiler refuses a
 * name in the schema that has no drawing here.
 */
export const DRAWERS: Readonly<Record<ResourceIcon, Drawer>> = {
  none: nothing,
  wheat,
  fish,
  cattle,
  horse,
  deer,
  ore,
  crystals,
  gem,
  spice,
  silk,
  incense,
  pearl,
  stone,
  oil,
};

/**
 * Draw a resource's picture centred on a tile.
 *
 * Anything on land stands on a soft shadow, which is what keeps it legible on busy
 * ground like forest; anything in water sits in a pale ripple instead, because a shadow
 * on the sea looks like a hole.
 */
export function drawResourceIcon(
  ctx: Ctx,
  icon: ResourceIcon,
  x: number,
  y: number,
  size: number,
  colour: string,
  water: boolean,
): void {
  if (icon === 'none') return;
  // About two-thirds of the tile's height. Smaller was tried first and the pictures were
  // correct but lost among the trees and ridges around them: a resource is the most
  // important thing on its tile and should be the biggest thing on it.
  const s = size * 0.38;

  ctx.save();
  if (water) {
    ctx.beginPath();
    ctx.ellipse(x, y + s * 0.78, s * 0.98, s * 0.24, 0, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(226, 245, 249, 0.5)';
    ctx.lineWidth = Math.max(1, s * 0.07);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.ellipse(x + s * 0.12, y + s * 0.84, s * 0.92, s * 0.22, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(20, 16, 10, 0.32)';
    ctx.fill();
  }
  DRAWERS[icon](ctx, x, y, s, colour.slice(0, 7));
  ctx.restore();
}
